import { useAsyncDebouncer } from "@tanstack/react-pacer/async-debouncer";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import type { WebUiConfig } from "@storyboard/web-client";
import { updateMembershipConfig } from "@storyboard/web-client";
import { transport } from "@/config";
import { createLogger } from "@/logger";
import { setNewMessageSoundConfig } from "@/models/newMessageSound";
import { UserContext } from "@/models/StateProviders/userProvider";
import {
  type CachedWebSettings,
  type DriverGridWebSettings,
  FALLBACK_WEB_SETTINGS,
  mergeUpdates,
  mergeWebSettings,
  resolveInitialSettings,
  type WebSettings,
  type WebSettingsUpdate,
  withServerDispatch,
} from "@/models/StateProviders/webSettingsMerge";
import { safeSetItem } from "@/utils/localStorage";

const log = createLogger("storage");

/**
 * Per-user web UI settings, server-persisted on the `WorkspaceMembershipConfig` cascade and delivered
 * fully compiled (defaults → workspace → member) on `/userinfo` as `config.web`. This provider reads
 * that compiled value, holds it in React state for the session, mirrors it to a `localStorage` cache
 * for instant first paint, and debounce-PATCHes edits back via `membershipsConfigPartialUpdate`.
 */

const HIDE_DRIVER_MESSAGES_KEY = "hideDriverMessages";
const PATCH_DEBOUNCE_MS = 600;

type WebSettingsState = {
  settings: WebSettings;
  updateWebSettings: (update: WebSettingsUpdate) => void;
  /**
   * Cancels the pending debounce and immediately persists any queued edits, resolving once the PATCH settles.
   * Call before a full page reload (e.g. Team Dispatching Mode's `forceReload`) so the debounced write isn't dropped.
   */
  flushWebSettings: () => Promise<void>;
  /**
   * Clears the member-level `driverGrid` override (PATCH `{ web: { driverGrid: null } }`) so column visibility,
   * order, sizing, and pinning fall back to the workspace's effective defaults, then applies the recompiled result.
   */
  resetDriverGrid: () => Promise<void>;
};

export const WebSettingsContext = createContext<WebSettingsState>({
  settings: FALLBACK_WEB_SETTINGS,
  updateWebSettings: () => {},
  flushWebSettings: async () => {},
  resetDriverGrid: async () => {},
});

function localStorageOrNull(): Storage | null {
  return typeof window === "undefined" ? null : window.localStorage;
}

function readJson<T>(storage: Storage | null, key: string): T | null {
  try {
    const raw = storage?.getItem(key);
    return raw != null ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

/**
 * Mirror `hideDriverMessages` (a.k.a. Team Dispatching Mode) to its legacy global localStorage key so the synchronous,
 * non-React read in `models/actions/calculateUnreads.tsx` keeps working without a refactor.
 */
function mirrorHideDriverMessages(value: boolean): void {
  const ls = localStorageOrNull();
  if (!ls) return;
  safeSetItem(ls, HIDE_DRIVER_MESSAGES_KEY, JSON.stringify(value));
}

/**
 * Reads the pre-migration localStorage keys for `workspaceId` and returns a sparse `web` partial.
 * Only keys actually present are included, so the one-time seed never overwrites a server default
 * with a fabricated value.
 */
function readLegacySettings(workspaceId: string): WebSettingsUpdate {
  if (typeof window === "undefined") return {};
  const ls = window.localStorage;
  const update: WebSettingsUpdate = {};

  const driverGrid: Partial<DriverGridWebSettings> = {};
  const columnVisibility = readJson<Record<string, boolean>>(ls, `${workspaceId}-driverGridColumns`);
  if (columnVisibility) driverGrid.columnVisibility = columnVisibility;
  const columnOrder = readJson<string[]>(ls, `${workspaceId}-driverGridColumnOrder`);
  if (columnOrder) driverGrid.columnOrder = columnOrder;
  const columnSizing = readJson<Record<string, number>>(ls, `${workspaceId}-driverGridColumnSizing`);
  if (columnSizing) driverGrid.columnSizing = columnSizing;
  if (Object.keys(driverGrid).length > 0) update.driverGrid = driverGrid;

  const driverView: Partial<WebSettings["driverView"]> = {};
  const tab = readJson<string>(ls, `${workspaceId}-activeTab`);
  if (tab) driverView.tab = tab;
  const sort = readJson<string>(ls, `${workspaceId}-channelSort`);
  if (sort) driverView.sort = sort;
  if (Object.keys(driverView).length > 0) update.driverView = driverView;

  const layout: Partial<WebSettings["layout"]> = {};
  const driverFeedPanels = readJson<{ table: number; feed: number }>(ls, "driver-feed-panels");
  if (driverFeedPanels) layout.driverFeedPanels = driverFeedPanels;
  const panelColumns = readJson<{ left: number; right: number }>(ls, "panel-columns");
  if (panelColumns) layout.panelColumns = panelColumns;
  if (Object.keys(layout).length > 0) update.layout = layout;

  const hideDriverMessages = readJson<boolean>(ls, HIDE_DRIVER_MESSAGES_KEY);
  if (typeof hideDriverMessages === "boolean") update.hideDriverMessages = hideDriverMessages;
  const userReadOnlyMode = readJson<boolean>(ls, "userReadOnlyMode");
  if (typeof userReadOnlyMode === "boolean") update.userReadOnlyMode = userReadOnlyMode;

  return update;
}

const WebSettingsProvider = ({ children }: { children: ReactNode }) => {
  const { workspaceId, workspaceMembership } = useContext(UserContext);
  const membershipId = workspaceMembership?.id ?? null;
  const serverWeb = workspaceMembership?.config?.web as WebUiConfig | undefined;
  const cacheKey = membershipId ? `${membershipId}-webSettingsCache` : null;

  const [settings, setSettings] = useState<WebSettings>(() =>
    resolveInitialSettings(cacheKey ? readJson<CachedWebSettings>(localStorageOrNull(), cacheKey) : null, serverWeb),
  );

  const settingsRef = useRef(settings);
  settingsRef.current = settings;

  // Mirror the sound settings into the sound module, whose caller (the AppSync item pipeline) lives outside React.
  useEffect(() => {
    setNewMessageSoundConfig({
      scope: settings.newMessageSoundScope,
      soundId: settings.newMessageSoundId,
      volume: settings.newMessageSoundVolume,
      myWorkspaceMembershipId: membershipId,
    });
  }, [settings.newMessageSoundScope, settings.newMessageSoundId, settings.newMessageSoundVolume, membershipId]);

  const pendingRef = useRef<WebSettingsUpdate>({});
  // The membership the provider currently serves, read at reply time: a reply for a membership since left must not
  // re-baseline the one that replaced it.
  const membershipIdRef = useRef(membershipId);
  membershipIdRef.current = membershipId;
  // PATCHes go out one at a time, in the order their edits were queued. The server reads, merges, and writes each
  // body, so two in flight at once could land in either order, and their replies re-baseline the block in whatever
  // order they arrive; a reset overtaken by the resize before it would come back undone.
  const inflightRef = useRef<Promise<void>>(Promise.resolve());

  const flushPatch = useCallback(async (): Promise<boolean> => {
    const pending = pendingRef.current;
    pendingRef.current = {};
    if (!workspaceId || !membershipId || Object.keys(pending).length === 0) return false;
    const previous = inflightRef.current;
    let settle = () => {};
    inflightRef.current = new Promise<void>((resolve) => {
      settle = resolve;
    });
    try {
      await previous;
      const response = await updateMembershipConfig(transport, {
        workspaceId,
        membershipId,
        config: { web: pending as WebUiConfig },
      });
      if (membershipIdRef.current !== membershipId) return true;
      // The Dispatch block comes back compiled through the whole cascade, which resolves what this client guessed at:
      // a key handed back to the workspace reads as the workspace's here, not as the view's.
      const rebaselined = withServerDispatch(settingsRef.current, response?.config?.web, pendingRef.current.dispatch);
      if (rebaselined !== settingsRef.current) {
        settingsRef.current = rebaselined;
        setSettings(rebaselined);
        if (cacheKey) {
          safeSetItem(window.localStorage, cacheKey, JSON.stringify(rebaselined));
        }
      }
      return true;
    } catch (error: unknown) {
      log.warn("Failed to persist web settings", { error: String(error) });
      return false;
    } finally {
      settle();
    }
  }, [workspaceId, membershipId, cacheKey]);

  const patchDebouncer = useAsyncDebouncer(flushPatch, {
    wait: PATCH_DEBOUNCE_MS,
    // `flushPatch` handles its own failures, so nothing should reach here. Wiring it anyway turns
    // `throwOnError` off, which keeps a rejection off the promise `updateWebSettings` never awaits.
    onError: (error) => log.warn("Web settings PATCH threw past its own handler", { error: String(error) }),
  });

  // Pulled off the instance rather than reached through `patchDebouncer`, whose object identity is only stable via the
  // hook's internal `useSelector` memoization. These three are arrow properties on the one instance the hook holds in
  // `useState`, so they are stable for the component's life and safe to depend on — which matters most for the
  // cleanup below, where a churning dependency would fire a PATCH on every render.
  const { cancel: cancelPendingPatch, flush: flushPendingPatch, maybeExecute: schedulePatch } = patchDebouncer;

  // Persist queued edits to the membership that owns them, on unmount and before every re-baseline. React runs all
  // destroy functions before any create function, so this fires ahead of the re-baseline effect below that clears
  // `pendingRef` — and because the cleanup was created on a render for the *previous* membership, the `flushPatch` it
  // closes over still carries that membership's ids. The debouncer's own `flush()` could not stand in here: the hook
  // rebinds `fn` during render, so by the time effects run it would PATCH the outgoing member's edits to the
  // incoming membership.
  useEffect(() => {
    return () => {
      cancelPendingPatch();
      void flushPatch();
    };
  }, [flushPatch, cancelPendingPatch]);

  // Re-baseline when the membership (and thus its compiled config) changes — e.g. switching workspaces. The cleanup
  // above has already flushed any queued edits to the outgoing membership by this point; clearing `pendingRef` here
  // is what stops a later debounced write from applying the previous member's edits to the newly-selected one.
  useEffect(() => {
    pendingRef.current = {};
    const cached = cacheKey ? readJson<CachedWebSettings>(localStorageOrNull(), cacheKey) : null;
    const resolved = resolveInitialSettings(cached, serverWeb);
    settingsRef.current = resolved;
    setSettings(resolved);
    mirrorHideDriverMessages(resolved.hideDriverMessages);
  }, [membershipId]);

  const flushWebSettings = useCallback(async (): Promise<void> => {
    await flushPendingPatch();
  }, [flushPendingPatch]);

  const resetDriverGrid = useCallback(async (): Promise<void> => {
    if (!workspaceId || !membershipId) return;
    // Drop any queued edits + the pending PATCH so a debounced write can't resurrect the cleared driverGrid override.
    cancelPendingPatch();
    pendingRef.current = {};
    try {
      const response = await updateMembershipConfig(transport, {
        workspaceId,
        membershipId,
        config: { web: { driverGrid: null } },
      });
      // The handler returns the recompiled (workspace-effective) config; replace driverGrid wholesale so stale member
      // overrides (order/sizing/pinning) are dropped rather than merged.
      const recompiled = response?.config?.web?.driverGrid ?? undefined;
      const nextDriverGrid: DriverGridWebSettings = {
        ...FALLBACK_WEB_SETTINGS.driverGrid,
        ...(recompiled as Partial<DriverGridWebSettings> | undefined),
      };
      const next: WebSettings = { ...settingsRef.current, driverGrid: nextDriverGrid };
      settingsRef.current = next;
      setSettings(next);
      if (cacheKey) {
        safeSetItem(window.localStorage, cacheKey, JSON.stringify(next));
      }
    } catch (error: unknown) {
      log.warn("Failed to reset driver grid settings", { error: String(error) });
    }
  }, [workspaceId, membershipId, cacheKey, cancelPendingPatch]);

  const updateWebSettings = useCallback(
    (update: WebSettingsUpdate) => {
      const next = mergeWebSettings(settingsRef.current, update);
      settingsRef.current = next;
      setSettings(next);

      if (cacheKey) {
        safeSetItem(window.localStorage, cacheKey, JSON.stringify(next));
      }
      if (typeof update.hideDriverMessages === "boolean") {
        mirrorHideDriverMessages(update.hideDriverMessages);
      }

      pendingRef.current = mergeUpdates(pendingRef.current, update);

      void schedulePatch();
    },
    [cacheKey, schedulePatch],
  );

  // One-time per-membership migration of legacy localStorage keys into server config. The sentinel is only set after a
  // successful PATCH (or when there is nothing to migrate), so a tab closed mid-flight re-attempts the seed next boot
  // rather than silently dropping the legacy values. Legacy keys are left in place for one release.
  useEffect(() => {
    if (!workspaceId || !membershipId) return;
    const ls = localStorageOrNull();
    if (!ls) return;
    const sentinelKey = `${membershipId}-settingsMigrated`;
    if (ls.getItem(sentinelKey) === "1") return;

    const legacy = readLegacySettings(workspaceId);
    if (Object.keys(legacy).length === 0) {
      ls.setItem(sentinelKey, "1");
      return;
    }

    updateWebSettings(legacy);
    cancelPendingPatch();
    flushPatch().then((ok) => {
      if (ok) ls.setItem(sentinelKey, "1");
    });
  }, [workspaceId, membershipId]);

  const value = useMemo<WebSettingsState>(
    () => ({ settings, updateWebSettings, flushWebSettings, resetDriverGrid }),
    [settings, updateWebSettings, flushWebSettings, resetDriverGrid],
  );

  return <WebSettingsContext.Provider value={value}>{children}</WebSettingsContext.Provider>;
};

export function useWebSettings(): WebSettingsState {
  return useContext(WebSettingsContext);
}

export default WebSettingsProvider;
